import { useState, useEffect, useCallback } from 'react'
import { Card, Row, Col, Button, Space, Tag, List, Skeleton, DatePicker, App } from 'antd'
import { ReloadOutlined, ArrowUpOutlined, ArrowDownOutlined, RightOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import * as echarts from 'echarts/core'
import { PieChart, LineChart, BarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import ReactECharts from 'echarts-for-react'
import MetricCard from '@/components/business/MetricCard'
import { getDashboardMetrics, refreshDashboardMetrics } from '@/services/dashboardService'
import type { DashboardData, TopIssueItem } from '@/types/dashboard'
import type { Dayjs } from 'dayjs'
import { useThemeStore } from '@/stores/themeStore'
import {
  getChartColor,
  getCommonEChartsTheme,
  getPieChartBorderColor,
  getAreaGradient
} from '@/utils/chartTheme'
import './Dashboard.css'

echarts.use([
  PieChart,
  LineChart,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  CanvasRenderer
])

const { RangePicker } = DatePicker

const Dashboard = () => {
  const { message } = App.useApp()
  const navigate = useNavigate()
  const { theme } = useThemeStore()
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null)

  // 加载仪表板数据
  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const dashboardData = await getDashboardMetrics()
      setData(dashboardData)
    } catch (error) {
      message.error('加载数据失败')
      console.error('Failed to load dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }, [message])

  // 刷新数据
  const handleRefresh = async () => {
    try {
      setRefreshing(true)
      const dashboardData = await refreshDashboardMetrics()
      setData(dashboardData)
      message.success('数据已刷新')
    } catch (error) {
      message.error('刷新失败')
    } finally {
      setRefreshing(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [loadData])

  // 日期范围变化时重新加载数据
  useEffect(() => {
    if (!dateRange) return
    const timer = window.setTimeout(() => {
      // TODO: 将 dateRange 传入 API 查询参数
      loadData()
    }, 300)
    return () => window.clearTimeout(timer)
  }, [dateRange, loadData])

  // 渠道分布饼图配置
  const getChannelPieOption = () => {
    if (!data) return {}

    const commonTheme = getCommonEChartsTheme(theme)
    const borderColor = getPieChartBorderColor(theme)

    return {
      ...commonTheme,
      tooltip: {
        ...commonTheme.tooltip,
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)'
      },
      legend: {
        ...commonTheme.legend,
        orient: 'vertical',
        left: 'right',
        top: 'middle'
      },
      series: [
        {
          name: '渠道分布',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 6,
            borderColor: borderColor,
            borderWidth: 2
          },
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 20,
              fontWeight: 'bold',
              color: theme === 'dark' ? '#FFFFFF' : '#1E293B'
            }
          },
          labelLine: {
            show: false
          },
          data: data.channelDistribution.map((item, index) => ({
            value: item.count,
            name: item.channelName,
            itemStyle: {
              color: getChartColor(index, theme)
            }
          }))
        }
      ]
    }
  }

  // 产品品类分布条形图配置
  const getCategoryBarOption = () => {
    if (!data) return {}

    const commonTheme = getCommonEChartsTheme(theme)
    const primaryColor = getChartColor(0, theme) // 使用第一个图表颜色
    const primaryLight = theme === 'dark' ? '#93C5FD' : '#60A5FA'

    return {
      ...commonTheme,
      tooltip: {
        ...commonTheme.tooltip,
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: (params: any) => {
          const param = Array.isArray(params) ? params[0] : params
          const item = data.categoryDistribution[param.dataIndex]
          return `${param.axisValue}<br/>数量: <strong>${param.value}</strong><br/>占比: ${item.percentage}%`
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '10px',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: data.categoryDistribution.map(item => item.category),
        ...commonTheme.xAxis
      },
      yAxis: {
        type: 'value',
        ...commonTheme.yAxis
      },
      series: [
        {
          type: 'bar',
          data: data.categoryDistribution.map(item => item.count),
          barWidth: '50%',
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: primaryLight },
              { offset: 1, color: primaryColor }
            ]),
            borderRadius: [4, 4, 0, 0]
          },
          label: {
            show: true,
            position: 'top',
            formatter: '{c}',
            color: theme === 'dark' ? '#B4B4B4' : '#64748B',
            fontSize: 12
          }
        }
      ]
    }
  }

  // 情感趋势折线图配置
  const getSentimentTrendOption = () => {
    if (!data) return {}

    const commonTheme = getCommonEChartsTheme(theme)
    const successColor = getChartColor(1, theme) // 绿色
    const primaryColor = getChartColor(0, theme) // 蓝色
    const errorColor = getChartColor(4, theme)   // 红色

    return {
      ...commonTheme,
      tooltip: {
        ...commonTheme.tooltip,
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        }
      },
      legend: {
        ...commonTheme.legend,
        data: ['正面', '中性', '负面'],
        top: 0
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: data.sentimentTrend.map(item =>
          new Date(item.date).toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' })
        ),
        ...commonTheme.xAxis
      },
      yAxis: {
        type: 'value',
        ...commonTheme.yAxis
      },
      series: [
        {
          name: '正面',
          type: 'line',
          smooth: true,
          data: data.sentimentTrend.map(item => item.positive),
          lineStyle: {
            width: 2
          },
          itemStyle: {
            color: successColor
          },
          areaStyle: getAreaGradient(successColor, theme),
          symbol: 'circle',
          symbolSize: 6
        },
        {
          name: '中性',
          type: 'line',
          smooth: true,
          data: data.sentimentTrend.map(item => item.neutral),
          lineStyle: {
            width: 2
          },
          itemStyle: {
            color: primaryColor
          },
          areaStyle: getAreaGradient(primaryColor, theme),
          symbol: 'circle',
          symbolSize: 6
        },
        {
          name: '负面',
          type: 'line',
          smooth: true,
          data: data.sentimentTrend.map(item => item.negative),
          lineStyle: {
            width: 2
          },
          itemStyle: {
            color: errorColor
          },
          areaStyle: getAreaGradient(errorColor, theme),
          symbol: 'circle',
          symbolSize: 6
        }
      ]
    }
  }

  // 渲染情感标签
  const renderSentimentTag = (sentiment: 'positive' | 'neutral' | 'negative') => {
    const config = {
      positive: { color: 'success', text: '正面' },
      neutral: { color: 'default', text: '中性' },
      negative: { color: 'error', text: '负面' }
    }
    const { color, text } = config[sentiment]
    return <Tag color={color}>{text}</Tag>
  }

  // 点击指标卡片跳转到分析工作台
  const handleMetricClick = (metricType: string) => {
    navigate('/analysis', { state: { presetMetric: metricType } })
  }

  // 点击问题项跳转到数据探索
  const handleIssueClick = (issue: TopIssueItem) => {
    navigate('/data', { state: { searchKeyword: issue.keyword } })
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-actions">
        <RangePicker
          value={dateRange}
          onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs] | null)}
          placeholder={['开始日期', '结束日期']}
          style={{ width: 280 }}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={refreshing}
        >
          刷新
        </Button>
      </div>

      {loading ? (
        <>
          <Row gutter={[24, 24]}>
            {[1, 2, 3, 4].map(i => (
              <Col xs={24} sm={12} lg={6} key={i}>
                <Card>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
              </Col>
            ))}
          </Row>
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24} lg={12}>
              <Card>
                <Skeleton active paragraph={{ rows: 6 }} />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card>
                <Skeleton active paragraph={{ rows: 6 }} />
              </Card>
            </Col>
          </Row>
        </>
      ) : data ? (
        <>
          {/* 关键指标卡片区 */}
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} lg={6}>
              <MetricCard
                data={data.overview.totalFeedback}
                onClick={() => handleMetricClick('feedback')}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <MetricCard
                data={data.overview.avgSatisfaction}
                onClick={() => handleMetricClick('satisfaction')}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <MetricCard
                data={data.overview.positiveRate}
                onClick={() => handleMetricClick('sentiment')}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <MetricCard
                data={data.overview.responseRate}
                onClick={() => handleMetricClick('response')}
              />
            </Col>
          </Row>

          {/* 图表区域 */}
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24} lg={16}>
              <Card
                title="情感趋势（最近7天）"
                variant="borderless"
                className="dashboard-card"
              >
                <ReactECharts
                  option={getSentimentTrendOption()}
                  style={{ height: 320 }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card
                title="渠道分布"
                variant="borderless"
                className="dashboard-card"
              >
                <ReactECharts
                  option={getChannelPieOption()}
                  style={{ height: 320 }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              </Card>
            </Col>
          </Row>

          {/* 产品品类分布 */}
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col span={24}>
              <Card
                title="产品品类分布"
                variant="borderless"
                className="dashboard-card"
              >
                <ReactECharts
                  option={getCategoryBarOption()}
                  style={{ height: 280 }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              </Card>
            </Col>
          </Row>

          {/* Top 5 热点问题 */}
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col span={24}>
              <Card
                title="Top 5 热点问题"
                variant="borderless"
                className="dashboard-card"
                extra={
                  <Button
                    type="link"
                    onClick={() => navigate('/data')}
                  >
                    查看更多 <RightOutlined />
                  </Button>
                }
              >
                <List
                  dataSource={data.topIssues}
                  renderItem={(item, index) => (
                    <List.Item
                      key={item.id}
                      onClick={() => handleIssueClick(item)}
                      className="issue-list-item"
                    >
                      <List.Item.Meta
                        avatar={
                          <div className="issue-rank-badge">
                            {index + 1}
                          </div>
                        }
                        title={
                          <Space>
                            <span className="issue-title">
                              {item.keyword}
                            </span>
                            {renderSentimentTag(item.sentiment)}
                          </Space>
                        }
                        description={
                          <div className="issue-meta">
                            <span>提及次数: {item.count}</span>
                            <span className={item.change >= 0 ? 'issue-change issue-change-up' : 'issue-change issue-change-down'}>
                              {item.change >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                              {Math.abs(item.change)}
                            </span>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>

          {/* 数据更新时间 */}
          <div className="dashboard-update-time">
            最后更新时间: {new Date(data.lastUpdateTime).toLocaleString('zh-CN')}
          </div>
        </>
      ) : (
        <Card className="dashboard-card">
          <div className="dashboard-empty">
            暂无数据
          </div>
        </Card>
      )}
    </div>
  )
}

export default Dashboard
